import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { T, sans, card, topBtn, SubdomainOverrideModal } from './VendorDetails';
import type { Subdomain } from './VendorDetails';

interface IpRow { ip_address: string; hostnames: string[]; owner: string | null; asn_name: string | null }

type Status = 'live' | 'dns' | 'none';
type View = 'all' | 'live' | 'dns' | 'scanned';
type SortKey = 'domain' | 'status' | 'network' | 'findings' | 'score';

interface Row {
  sub: Subdomain;
  host: string;
  primary: boolean;
  status: Status;
  ips: string[];
  network: string | null;
}

interface Props {
  vendorId: string;
  vendorDomain: string;
  isDark: boolean;
}

const APP_HEADER_HEIGHT = 64;
const PANEL_SLIDE_MS = 220;
const STATUS_LABEL: Record<Status, string> = { live: 'Live', dns: 'DNS records only', none: 'No address found' };
const STATUS_RANK: Record<Status, number> = { live: 0, dns: 1, none: 2 };
const ENV_LABEL: Record<string, string> = {
  production: 'Production', non_production: 'Non-production', internal: 'Internal', unknown: 'Unknown',
};

export const subdomainsKey = (vendorId: string, domain: string) => ['vendors', 'detail', vendorId, 'subdomains', domain] as const;
const ipAddressesKey = (vendorId: string) => ['vendors', 'detail', vendorId, 'ip-addresses'] as const;

async function fetchDomains(domain: string): Promise<{ scanId: string | null; scanDate: string | null; subdomains: Subdomain[] }> {
  const scans = (await api.get(`/v1/scans/by-domain/${domain}`)).data.scans || [];
  if (!scans.length) return { scanId: null, scanDate: null, subdomains: [] };
  const scanId = scans[0].scan_id || scans[0].id;
  const { data } = await api.get(`/v1/vendors/subdomains/${scanId}`);
  return { scanId, scanDate: scans[0].completed_at || scans[0].created_at || null, subdomains: data.subdomains || [] };
}

const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};
const fmtDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const scoreColor = (s: number, isDark: boolean) =>
  s >= 80 ? (isDark ? '#4ade80' : T.grn) : s >= 60 ? (isDark ? '#fbbf24' : T.amb) : s >= 40 ? '#EA580C' : (isDark ? '#f87171' : T.red);
const csvCell = (value: unknown) => {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export default function VendorDomains({ vendorId, vendorDomain, isDark }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';

  const line = isDark ? '#1f1f1f' : T.bdr;
  const ink = isDark ? '#e5e7eb' : T.ink;
  const slate = isDark ? '#94a3b8' : T.slate;
  const muted = isDark ? '#64748b' : T.muted;
  const surface = isDark ? '#0d0d0d' : T.surface;
  const surface2 = isDark ? '#141414' : T.surface2;
  const rowLine = isDark ? '#161616' : '#F1F5F9';
  const accent = isDark ? '#60A5FA' : T.blue;
  const green = isDark ? '#4ade80' : '#16A34A';
  const statusColor = (s: Status) => (s === 'live' ? green : s === 'dns' ? slate : muted);

  const [view, setView] = useState<View>('all');
  const [search, setSearch] = useState('');
  const [networkFilter, setNetworkFilter] = useState('');
  const [findingsOnly, setFindingsOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('domain');
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<string | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [editing, setEditing] = useState<Subdomain | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'ok' | 'error' } | null>(null);
  const panelTimer = useRef<number | undefined>(undefined);

  const domainsQuery = useQuery({
    queryKey: subdomainsKey(vendorId, vendorDomain),
    queryFn: () => fetchDomains(vendorDomain),
    enabled: !!vendorDomain,
  });
  // Shares its cache entry with the IP Addresses tab; it only reads what that tab's lookups already stored.
  const ipQuery = useQuery({
    queryKey: ipAddressesKey(vendorId),
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/ip-addresses`)).data as { addresses: IpRow[] },
  });

  const data = domainsQuery.data ?? null;
  const scanId = data?.scanId ?? null;

  // The scan payload gives an address only for discovered hosts and a provider that is usually just "Other"; the
  // IP Addresses data maps every hostname to its addresses and their real network owner.
  const rows: Row[] = useMemo(() => {
    const byHost = new Map<string, IpRow[]>();
    for (const ip of ipQuery.data?.addresses ?? []) {
      for (const host of ip.hostnames || []) {
        const key = host.toLowerCase();
        byHost.set(key, [...(byHost.get(key) || []), ip]);
      }
    }
    return (data?.subdomains ?? []).map(sub => {
      const host = sub.subdomain.toLowerCase();
      const known = byHost.get(host) || [];
      const ips = Array.from(new Set([...(sub.ip ? [sub.ip] : []), ...known.map(k => k.ip_address)]));
      const owners = Array.from(new Set(known.map(k => k.owner).filter(Boolean) as string[]));
      const network = owners[0] || (sub.provider && sub.provider !== 'Other' ? sub.provider : null);
      const live = ips.length > 0 || (!!sub.scanned && sub.resolves !== false);
      const status: Status = live ? 'live' : sub.records && sub.records.length ? 'dns' : 'none';
      return { sub, host: sub.subdomain, primary: host === vendorDomain.toLowerCase(), status, ips, network };
    });
  }, [data, ipQuery.data, vendorDomain]);

  const counts = useMemo(() => ({
    all: rows.length,
    live: rows.filter(r => r.status === 'live').length,
    dns: rows.filter(r => r.status === 'dns').length,
    none: rows.filter(r => r.status === 'none').length,
    scanned: rows.filter(r => r.sub.scanned).length,
    withFindings: rows.filter(r => r.sub.scanned && r.sub.findings_count > 0).length,
    findings: rows.reduce((sum, r) => sum + (r.sub.scanned ? r.sub.findings_count : 0), 0),
  }), [rows]);

  const networks = useMemo(() => {
    const tally = new Map<string, number>();
    rows.forEach(r => { if (r.network) tally.set(r.network, (tally.get(r.network) || 0) + 1); });
    return Array.from(tally.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [rows]);

  const query = search.trim().toLowerCase();
  const visible = useMemo(() => {
    const filtered = rows.filter(r =>
      (view === 'all' || (view === 'scanned' ? r.sub.scanned : r.status === view)) &&
      (!networkFilter || r.network === networkFilter) &&
      (!findingsOnly || (r.sub.scanned && r.sub.findings_count > 0)) &&
      (!query || r.host.toLowerCase().includes(query) || r.ips.some(ip => ip.includes(query)) || (r.network || '').toLowerCase().includes(query)),
    );
    const dir = sortAsc ? 1 : -1;
    const byDomain = (a: Row, b: Row) => Number(b.primary) - Number(a.primary) || a.host.localeCompare(b.host);
    const compare: Record<SortKey, (a: Row, b: Row) => number> = {
      domain: (a, b) => (a.primary !== b.primary ? Number(b.primary) - Number(a.primary) : dir * a.host.localeCompare(b.host)),
      status: (a, b) => dir * (STATUS_RANK[a.status] - STATUS_RANK[b.status]) || byDomain(a, b),
      network: (a, b) => dir * (a.network || '~').localeCompare(b.network || '~') || byDomain(a, b),
      findings: (a, b) => dir * ((b.sub.scanned ? b.sub.findings_count : -1) - (a.sub.scanned ? a.sub.findings_count : -1)) || byDomain(a, b),
      score: (a, b) => dir * ((a.sub.overall_score ?? 999) - (b.sub.overall_score ?? 999)) || byDomain(a, b),
    };
    return filtered.sort(compare[sortKey]);
  }, [rows, view, networkFilter, findingsOnly, query, sortKey, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const selectedRow = rows.find(r => r.host === selected) ?? null;

  const saveOverride = useMutation({
    mutationFn: (body: { subdomain: string; environment: string; reason: string | null }) =>
      api.post('/v1/vendors/subdomain/override', { scan_id: scanId, ...body }),
    meta: { invalidates: [subdomainsKey(vendorId, vendorDomain)] },
  });
  const revertOverride = useMutation({
    mutationFn: (subdomain: string) =>
      api.delete('/v1/vendors/subdomain/override', { params: { scan_id: scanId, subdomain } }),
    meta: { invalidates: [subdomainsKey(vendorId, vendorDomain)] },
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);
  useEffect(() => () => window.clearTimeout(panelTimer.current), []);

  const resetPaging = () => setPage(1);
  const openPanel = (host: string) => { window.clearTimeout(panelTimer.current); setSelected(host); setPanelVisible(true); };
  const closePanel = () => {
    setPanelVisible(false);
    window.clearTimeout(panelTimer.current);
    panelTimer.current = window.setTimeout(() => setSelected(null), PANEL_SLIDE_MS);
  };
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc); else { setSortKey(key); setSortAsc(true); }
    resetPaging();
  };
  const clearFilters = () => { setSearch(''); setNetworkFilter(''); setFindingsOnly(false); setView('all'); resetPaging(); };
  const filtersActive = !!(query || networkFilter || findingsOnly || view !== 'all');

  const handleSaveOverride = async (environment: string, reason: string) => {
    if (!editing) return;
    try {
      await saveOverride.mutateAsync({ subdomain: editing.subdomain, environment, reason: reason || null });
      setToast({ message: `${editing.subdomain} set to ${ENV_LABEL[environment] || environment}`, tone: 'ok' });
      setEditing(null);
    } catch (error) {
      setToast({ message: apiError(error, 'Could not change the environment.'), tone: 'error' });
    }
  };
  const handleRevert = async (sub: Subdomain) => {
    if (!window.confirm(`Revert ${sub.subdomain} to its automatically detected environment?`)) return;
    try {
      await revertOverride.mutateAsync(sub.subdomain);
      setToast({ message: `${sub.subdomain} reverted to automatic`, tone: 'ok' });
    } catch (error) {
      setToast({ message: apiError(error, 'Could not revert the environment.'), tone: 'error' });
    }
  };

  const exportCsv = () => {
    const header = ['Domain', 'Primary', 'Status', 'IP addresses', 'Network', 'DNS records', 'Scanned in full', 'Findings', 'Score', 'Risk level', 'Environment', 'Environment set by'];
    const lines = visible.map(r => [
      r.host, r.primary ? 'Yes' : '', STATUS_LABEL[r.status], r.ips.join(' '), r.network || '', (r.sub.records || []).join(' '),
      r.sub.scanned ? 'Yes' : 'No', r.sub.scanned ? r.sub.findings_count : '', r.sub.overall_score ?? '', r.sub.risk_level || '',
      ENV_LABEL[r.sub.environment] || r.sub.environment, r.sub.is_override ? 'Manual override' : 'Automatic',
    ]);
    const csv = [header, ...lines].map(l => l.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${vendorDomain}-domains-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /* ── Loading / error / empty ─────────────────────────────────── */

  if (!data && domainsQuery.isPending) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
          {[0, 1, 2, 3, 4].map(i => <div key={i} style={{ ...card(isDark), height: 84 }} />)}
        </div>
        <div style={{ ...card(isDark), height: 360 }} />
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ ...card(isDark), padding: 32, textAlign: 'center', fontFamily: sans }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: ink, marginBottom: 6 }}>Couldn't load domains</div>
        <div style={{ fontSize: 13, color: slate, marginBottom: 14 }}>{apiError(domainsQuery.error, 'Something went wrong.')}</div>
        <button onClick={() => domainsQuery.refetch()} style={{ ...topBtn(false, isDark), margin: '0 auto' }}>Try again</button>
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div style={{ ...card(isDark), padding: '48px 24px', textAlign: 'center', fontFamily: sans }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: ink, marginBottom: 4 }}>No domains yet</div>
        <div style={{ fontSize: 13, color: slate }}>Domains and subdomains appear here once a scan of {vendorDomain} finishes.</div>
      </div>
    );
  }

  /* ── Pieces ──────────────────────────────────────────────────── */

  const kpiLabel: React.CSSProperties = { fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: muted };
  const kpiValue = (color: string): React.CSSProperties => ({ fontSize: 22, fontWeight: 700, color, marginTop: 6, lineHeight: 1 });
  const kpiNote: React.CSSProperties = { fontSize: 11, color: slate, marginTop: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const th: React.CSSProperties = {
    textAlign: 'left', fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: muted,
    padding: '9px 14px', background: surface2, borderBottom: `1px solid ${line}`, whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = { fontSize: 12, padding: '10px 14px', borderBottom: `1px solid ${rowLine}`, verticalAlign: 'middle', color: ink };
  const sortHeader = (key: SortKey, label: string, width?: number) => (
    <th style={{ ...th, width, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(key)}>
      {label}{sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : ''}
    </th>
  );
  const control: React.CSSProperties = {
    fontSize: 12, padding: '7px 10px', borderRadius: 6, border: `1px solid ${line}`, background: surface, color: ink, fontFamily: sans, cursor: 'pointer',
  };
  const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, color: accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: sans };
  const viewTab = (key: View, label: string, count: number) => {
    const active = view === key;
    return (
      <button key={key} onClick={() => { setView(key); resetPaging(); }} style={{
        background: 'none', border: 'none', borderBottom: `2px solid ${active ? ink : 'transparent'}`, marginBottom: -1,
        padding: '11px 12px', fontSize: 12, fontWeight: 600, color: active ? ink : slate, cursor: 'pointer', fontFamily: sans,
      }}>
        {label}
        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: isDark ? '#1a1a1a' : '#F1F5F9', color: slate, marginLeft: 6 }}>{count}</span>
      </button>
    );
  };
  const StatusCell = ({ status }: { status: Status }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: statusColor(status), whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: status === 'none' ? line : statusColor(status) }} />
      {STATUS_LABEL[status]}
    </span>
  );
  const AddressCell = ({ row }: { row: Row }) => {
    if (row.ips.length) {
      return (
        <div>
          <div style={{ fontSize: 12 }}>
            {row.ips[0]}{row.ips.length > 1 && <span style={{ color: muted, fontFamily: sans }}> +{row.ips.length - 1}</span>}
          </div>
          <div style={{ fontSize: 11, color: muted }}>{row.network || 'Network not looked up'}</div>
        </div>
      );
    }
    if (row.sub.records && row.sub.records.length) {
      return (
        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
          {row.sub.records.map(r => <span key={r} style={recordChip}>{r}</span>)}
        </span>
      );
    }
    return <span style={{ color: muted }}>—</span>;
  };
  const recordChip: React.CSSProperties = {
    fontSize: 11, padding: '1px 7px', borderRadius: 4, background: isDark ? '#1a1a1a' : '#F1F5F9', color: isDark ? '#cbd5e1' : '#334155',
  };
  const PrimaryTag = () => (
    <span style={{ fontSize: 9.5, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: isDark ? '#0c1d33' : '#EFF6FF', color: isDark ? '#93c5fd' : '#1D4ED8', marginLeft: 6 }}>Primary</span>
  );
  const isActiveRow = (host: string) => panelVisible && selected === host;

  const topNetwork = networks[0];

  return (
    <div style={{ fontFamily: sans }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: slate }}>
          Every domain and subdomain found for {vendorDomain} in the latest scan{data.scanDate ? ` · ${fmtDate(data.scanDate)}` : ''}
        </div>
        <button onClick={exportCsv} disabled={!visible.length} style={{ ...topBtn(false, isDark), opacity: visible.length ? 1 : 0.5 }}>Export CSV</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
        <div style={{ ...card(isDark), padding: '12px 14px' }}>
          <div style={kpiLabel}>Domains</div>
          <div style={kpiValue(ink)}>{counts.all}</div>
          <div style={kpiNote}>Found in the latest scan</div>
        </div>
        <div style={{ ...card(isDark), padding: '12px 14px' }}>
          <div style={kpiLabel}>Live</div>
          <div style={kpiValue(counts.live ? green : ink)}>{counts.live}</div>
          <div style={kpiNote}>Answer with a web address</div>
        </div>
        <div style={{ ...card(isDark), padding: '12px 14px' }}>
          <div style={kpiLabel}>Scanned in full</div>
          <div style={kpiValue(ink)}>{counts.scanned}</div>
          <div style={kpiNote}>{counts.all - counts.scanned ? `${counts.all - counts.scanned} discovered only` : 'Every domain scanned'}</div>
        </div>
        <div style={{ ...card(isDark), padding: '12px 14px' }}>
          <div style={kpiLabel}>With findings</div>
          <div style={kpiValue(counts.withFindings ? (isDark ? '#fbbf24' : '#D97706') : ink)}>{counts.withFindings}</div>
          <div style={kpiNote}>{counts.findings ? `${counts.findings} findings in total` : 'No findings'}</div>
        </div>
        <div style={{ ...card(isDark), padding: '12px 14px' }}>
          <div style={kpiLabel}>Networks</div>
          <div style={kpiValue(ink)}>{networks.length || '—'}</div>
          <div style={kpiNote} title={topNetwork ? topNetwork[0] : undefined}>
            {topNetwork ? `Mostly ${topNetwork[0]} (${topNetwork[1]})` : 'Not looked up yet'}
          </div>
        </div>
      </div>

      <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${line}`, padding: '0 14px' }}>
          {viewTab('all', 'All', counts.all)}
          {viewTab('live', 'Live', counts.live)}
          {viewTab('dns', 'DNS records only', counts.dns)}
          {viewTab('scanned', 'Scanned', counts.scanned)}
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '12px 14px', borderBottom: `1px solid ${line}`, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 320 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={muted} strokeWidth="2" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}>
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
            </svg>
            <input placeholder="Search domains, addresses or networks…" value={search} onChange={e => { setSearch(e.target.value); resetPaging(); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '7px 11px 7px 31px', fontSize: 12, fontFamily: sans, color: ink, background: surface, border: `1px solid ${line}`, borderRadius: 6, outline: 'none' }} />
          </div>
          {networks.length > 0 && (
            <select value={networkFilter} onChange={e => { setNetworkFilter(e.target.value); resetPaging(); }} style={control}>
              <option value="">All networks</option>
              {networks.map(([name, n]) => <option key={name} value={name}>{name} ({n})</option>)}
            </select>
          )}
          <button onClick={() => { setFindingsOnly(!findingsOnly); resetPaging(); }} style={{
            fontSize: 11, fontWeight: 600, padding: '6px 11px', borderRadius: 99, cursor: 'pointer', fontFamily: sans,
            border: `1px solid ${findingsOnly ? ink : line}`, background: findingsOnly ? ink : surface, color: findingsOnly ? (isDark ? '#0d0d0d' : '#fff') : slate,
          }}>Has findings</button>
        </div>

        {visible.length === 0 ? (
          <div style={{ padding: '52px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: ink, marginBottom: 4 }}>No domains match these filters</div>
            {filtersActive && <button onClick={clearFilters} style={{ ...linkBtn, fontSize: 12.5 }}>Clear filters</button>}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {sortHeader('domain', 'Domain')}
                  {sortHeader('status', 'Status', 150)}
                  {sortHeader('network', 'Address')}
                  {sortHeader('findings', 'Findings', 90)}
                  {sortHeader('score', 'Score', 100)}
                  {canEdit && <th style={{ ...th, width: 190, textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {pageRows.map(row => (
                  <tr key={row.host} data-domain-row onClick={() => openPanel(row.host)}
                    style={{ cursor: 'pointer', background: isActiveRow(row.host) ? (isDark ? '#0c1d33' : '#EFF6FF') : undefined }}
                    onMouseEnter={e => { if (!isActiveRow(row.host)) e.currentTarget.style.background = surface2; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isActiveRow(row.host) ? (isDark ? '#0c1d33' : '#EFF6FF') : ''; }}>
                    <td style={td}>
                      <span style={{ fontSize: 13, wordBreak: 'break-all' }}>{row.host}</span>
                      {row.primary && <PrimaryTag />}
                    </td>
                    <td style={td}><StatusCell status={row.status} /></td>
                    <td style={td}><AddressCell row={row} /></td>
                    <td style={td} title={row.sub.scanned ? undefined : 'Discovered only; not scanned in full'}>
                      {row.sub.scanned ? row.sub.findings_count : <span style={{ color: muted }}>—</span>}
                    </td>
                    <td style={td}>
                      {row.sub.overall_score != null ? (
                        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: scoreColor(row.sub.overall_score, isDark) }}>{row.sub.overall_score}</span>
                          {row.sub.risk_level && <span style={{ fontSize: 10.5, color: muted }}>{row.sub.risk_level}</span>}
                        </span>
                      ) : <span style={{ fontSize: 11, color: muted }}>Not scanned</span>}
                    </td>
                    {canEdit && (
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => setEditing(row.sub)} style={linkBtn}>Change environment</button>
                        {row.sub.is_override && (
                          <button onClick={() => handleRevert(row.sub)} style={{ ...linkBtn, color: isDark ? '#f87171' : T.red, marginLeft: 12 }}>Revert</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {visible.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 14px', fontSize: 11.5, color: slate, flexWrap: 'wrap', gap: 8 }}>
            <span>Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, visible.length)} of {visible.length}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} style={{ ...control, padding: '5px 10px', opacity: currentPage === 1 ? 0.5 : 1 }}>‹ Prev</button>
              <span>Page {currentPage} of {totalPages}</span>
              <button disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} style={{ ...control, padding: '5px 10px', opacity: currentPage === totalPages ? 0.5 : 1 }}>Next ›</button>
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); resetPaging(); }} style={{ ...control, padding: '5px 8px' }}>
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
              </select>
            </span>
          </div>
        )}
      </div>

      {selectedRow && (
        <DomainPanel
          row={selectedRow}
          scanId={scanId}
          scanDate={data.scanDate}
          isDark={isDark}
          canEdit={canEdit}
          visible={panelVisible}
          onClose={closePanel}
          onOpenScan={() => scanId && navigate(`/scans/${scanId}/results`)}
          onChangeEnvironment={() => setEditing(selectedRow.sub)}
          onRevert={() => handleRevert(selectedRow.sub)}
          onToast={message => setToast({ message, tone: 'ok' })}
          palette={{ line, ink, slate, muted, surface, surface2, accent, green }}
          statusColor={statusColor}
          recordChip={recordChip}
        />
      )}

      {editing && createPortal(
        <div data-domain-modal style={{ position: 'relative', zIndex: 1300 }}>
          <SubdomainOverrideModal
            open
            onClose={() => setEditing(null)}
            subdomain={editing}
            currentEnv={editing.environment || 'unknown'}
            onSave={handleSaveOverride}
            saving={saveOverride.isPending}
            isDark={isDark}
          />
        </div>,
        document.body,
      )}

      {toast && createPortal(
        <div role="status" style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 1400, padding: '10px 14px', borderRadius: 8, fontSize: 12.5, fontFamily: sans,
          background: surface, color: ink, border: `1px solid ${toast.tone === 'error' ? T.red : line}`,
          boxShadow: isDark ? '0 8px 30px rgba(0,0,0,.8)' : '0 8px 24px rgba(15,23,42,.12)',
        }}>
          <span style={{ color: toast.tone === 'error' ? T.red : T.grn, fontWeight: 700, marginRight: 8 }}>{toast.tone === 'error' ? '!' : '✓'}</span>{toast.message}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ─── Detail drawer ───────────────────────────────────────────────── */

function DomainPanel({
  row, scanId, scanDate, isDark, canEdit, visible, onClose, onOpenScan, onChangeEnvironment, onRevert, onToast, palette, statusColor, recordChip,
}: {
  row: Row;
  scanId: string | null;
  scanDate: string | null;
  isDark: boolean;
  canEdit: boolean;
  visible: boolean;
  onClose: () => void;
  onOpenScan: () => void;
  onChangeEnvironment: () => void;
  onRevert: () => void;
  onToast: (message: string) => void;
  palette: { line: string; ink: string; slate: string; muted: string; surface: string; surface2: string; accent: string; green: string };
  statusColor: (s: Status) => string;
  recordChip: React.CSSProperties;
}) {
  const { line, ink, slate, muted, surface, accent } = palette;
  const panelRef = useRef<HTMLElement>(null);
  const { sub } = row;

  // Outside clicks close the drawer; a click on another domain row switches to it, and the environment dialog keeps it open.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (panelRef.current?.contains(target) || target.closest('[data-domain-row], [data-domain-modal]')) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('[data-domain-modal]')) onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const label: React.CSSProperties = { fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: muted };
  const section: React.CSSProperties = { padding: '16px 20px', borderBottom: `1px solid ${line}` };
  const kvGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 10px', fontSize: 12.5, marginTop: 10 };
  const btn: React.CSSProperties = {
    fontFamily: sans, fontSize: 12, fontWeight: 600, padding: '8px 12px', borderRadius: 6, border: `1px solid ${line}`, background: surface, color: ink, cursor: 'pointer',
  };
  const pill = (text: string, bg: string, color: string) => (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: bg, color, whiteSpace: 'nowrap' }}>{text}</span>
  );
  const statusBg = row.status === 'live' ? (isDark ? '#132a1b' : '#F0FDF4') : (isDark ? '#1a1a1a' : '#F1F5F9');

  const copyDomain = async () => {
    try { await navigator.clipboard.writeText(row.host); onToast('Domain copied'); }
    catch { window.prompt('Copy this domain', row.host); }
  };

  return createPortal(
    <aside ref={panelRef} data-domain-panel role="dialog" aria-label={`${row.host} details`} style={{
      position: 'fixed', top: APP_HEADER_HEIGHT, right: 0, bottom: 0, width: 'min(430px, 100vw)', zIndex: 1150, background: surface,
      borderLeft: `1px solid ${line}`, display: 'flex', flexDirection: 'column', fontFamily: sans,
      animation: `${visible ? 'domainPanelIn' : 'domainPanelOut'} ${PANEL_SLIDE_MS}ms ${visible ? 'ease-out' : 'ease-in'} forwards`,
    }}>
      <style>{`
        @keyframes domainPanelIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes domainPanelOut { from { transform: translateX(0); } to { transform: translateX(100%); } }
        @media (prefers-reduced-motion: reduce) { aside[data-domain-panel] { animation-duration: 1ms !important; } }
      `}</style>

      <div style={section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {pill(STATUS_LABEL[row.status], statusBg, statusColor(row.status))}
            {row.primary && pill('Primary', isDark ? '#0c1d33' : '#EFF6FF', isDark ? '#93c5fd' : '#1D4ED8')}
          </div>
          <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: muted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: ink, marginTop: 12, wordBreak: 'break-all' }}>{row.host}</div>
        <div style={{ fontSize: 12, color: slate, marginTop: 3 }}>
          {sub.scanned ? `Scanned in full${scanDate ? ` in the ${fmtDate(scanDate)} scan` : ''}` : 'Discovered only: found in certificates and DNS, not scanned in full'}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ ...section, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          <div>
            <div style={label}>Score</div>
            {sub.overall_score != null ? (
              <div style={{ fontSize: 20, fontWeight: 700, color: scoreColor(sub.overall_score, isDark), marginTop: 4 }}>
                {sub.overall_score} <span style={{ fontSize: 12, fontWeight: 500, color: muted }}>/ 100{sub.risk_level ? ` · ${sub.risk_level}` : ''}</span>
              </div>
            ) : <div style={{ fontSize: 13, color: muted, marginTop: 6 }}>Not scanned</div>}
          </div>
          <div>
            <div style={label}>Findings</div>
            {sub.scanned
              ? <div style={{ fontSize: 20, fontWeight: 700, color: ink, marginTop: 4 }}>{sub.findings_count}</div>
              : <div style={{ fontSize: 13, color: muted, marginTop: 6 }}>Not scanned</div>}
          </div>
        </div>

        <div style={section}>
          <div style={label}>Address</div>
          <div style={kvGrid}>
            <span style={{ color: slate }}>IP address{row.ips.length > 1 ? 'es' : ''}</span>
            <span style={{ color: row.ips.length ? ink : muted, wordBreak: 'break-all' }}>{row.ips.length ? row.ips.join(', ') : 'None found'}</span>
            <span style={{ color: slate }}>Network</span>
            <span style={{ color: row.network ? ink : muted }}>{row.network || (row.ips.length ? 'Not looked up yet' : '—')}</span>
            {sub.records && sub.records.length > 0 && (
              <>
                <span style={{ color: slate }}>DNS records</span>
                <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{sub.records.map(r => <span key={r} style={recordChip}>{r}</span>)}</span>
              </>
            )}
          </div>
        </div>

        <div style={{ ...section, borderBottom: 'none' }}>
          <div style={label}>Environment</div>
          <div style={kvGrid}>
            <span style={{ color: slate }}>Environment</span>
            <span style={{ color: ink }}>{ENV_LABEL[sub.environment] || sub.environment}</span>
            <span style={{ color: slate }}>Set by</span>
            <span style={{ color: ink }}>
              {sub.is_override ? `Manual override (detected as ${ENV_LABEL[sub.auto_environment] || sub.auto_environment})` : 'Detected automatically'}
            </span>
            {sub.is_override && sub.override_reason && (
              <>
                <span style={{ color: slate }}>Reason</span>
                <span style={{ color: ink }}>{sub.override_reason}</span>
              </>
            )}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 14, marginTop: 12 }}>
              <button onClick={onChangeEnvironment} style={{ background: 'none', border: 'none', padding: 0, color: accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>Change environment</button>
              {sub.is_override && (
                <button onClick={onRevert} style={{ background: 'none', border: 'none', padding: 0, color: isDark ? '#f87171' : T.red, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>Revert to automatic</button>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '14px 20px', borderTop: `1px solid ${line}`, display: 'grid', gap: 8 }}>
        {scanId && (
          <button onClick={onOpenScan} style={{ ...btn, background: ink, color: isDark ? '#0d0d0d' : '#fff', border: `1px solid ${ink}`, padding: 9 }}>View in scan report</button>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {row.status === 'live'
            ? <a href={`https://${row.host}`} target="_blank" rel="noopener noreferrer" style={{ ...btn, flex: 1, textAlign: 'center', textDecoration: 'none' }}>Open site ↗</a>
            : <span style={{ ...btn, flex: 1, textAlign: 'center', color: muted, cursor: 'default' }} title="This domain has no web address">Open site</span>}
          <button onClick={copyDomain} style={{ ...btn, flex: 1 }}>Copy domain</button>
        </div>
      </div>
    </aside>,
    document.body,
  );
}
